import type { Editor } from "@tiptap/core";
import type { IntegrationType } from "@v2/features/settings/shop/integrations/IntegrationForm";
import type { Asset } from "schemas/generated/asset";
import type { Product } from "schemas/generated/product";

import React from "react";

import {
  selectAsset,
  selectIntegration,
  selectProduct,
} from "@agent-ui/features/chat/utils/contextItem";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@replo/design-system/components/tabs/Tabs";
import { skipToken } from "@tanstack/react-query";
import { ACCEPT_FILE_EXTENSIONS } from "@v2/features/assets/constants";
import { useReploFileDropZone } from "@v2/features/assets/useFileDropZone";
import { useChatFileDropZone } from "@v2/features/chat/hooks/useChatFileDropZone";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { useProductList } from "@v2/hooks/useProductList";
import { agentIntegrationsRouter } from "@v2/trpc/routes/agentIntegrations";
import { assetUtils } from "@v2/trpc/routes/asset";
import { integrationRouter } from "@v2/trpc/routes/integration";
import { ImageIcon, Plug, Search, ShoppingBag, Upload, X } from "lucide-react";
import { motion } from "motion/react";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";
import { z } from "zod";

import { AssetContextItemsList } from "./AssetContextItemsList";
import { IntegrationContextItemsList } from "./IntegrationContextItemsList";
import { IntegrationObjectTab } from "./IntegrationObjectTab";
import { ProductContextItemsList } from "./ProductContextItemsList";

const INITIAL_ITEMS_TO_LOAD = 10;

interface ContextItemsMenuProps {
  onClose?: () => void;
  editor: Editor | null;
  hideHeader?: boolean;
  inputSize?: "sm" | "base";
}

export const ContextItemsMenu = ({
  onClose,
  editor,
  hideHeader,
  inputSize = "sm",
}: ContextItemsMenuProps) => {
  const projectId = useCurrentProjectId();

  const [storedTab, setStoredTab] = useLocalStorageState(
    "replo.editor.contextItemsMenu.currentTab",
    "products",
    { schema: z.string() },
  );
  const [searchInputValue, searchQuery, setSearchInputValue] =
    useDebouncedState("");
  const menuRef = React.useRef<HTMLDivElement>(null);

  const isExternalUploadRef = React.useRef(false);

  const { data: mentionableObjects } =
    agentIntegrationsRouter.getMentionableObjects.useQuery(
      projectId ? { projectId } : skipToken,
    );

  const hasIntegrationObjects = (mentionableObjects?.length ?? 0) > 0;

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    droppedFilesMap,
    openFileDialog,
    uploadFiles,
  } = useReploFileDropZone({
    onUploadComplete: ({ url, file, id }) => {
      void assetUtils.list.invalidate();
      if (isExternalUploadRef.current && file && id) {
        isExternalUploadRef.current = false;
        const asset: Asset = {
          id,
          url,
          name: file.name,
          type: file.type.startsWith("video/") ? "video" : "image",
          fileExtension: file.name.split(".").pop() ?? "",
          createdAt: new Date(),
          updatedAt: new Date(),
          sizeBytes: file.size,
          width: null,
          height: null,
          projectId: projectId ?? "",
          altText: null,
          embeddingText: null,
        };
        selectAsset(asset, editor);
        onClose?.();
      }
    },
    acceptDropAssetType: ACCEPT_FILE_EXTENSIONS,
    allowClickToUpload: true,
    isDisabled: true,
  });

  useChatFileDropZone({
    isActive: true,
    acceptedTypes: ["image", "video"],
    onFileDrop: (files) => {
      setCurrentTab("assets");
      isExternalUploadRef.current = true;
      uploadFiles(files);
    },
  });

  const hasIntegrationProductTab =
    hasIntegrationObjects &&
    mentionableObjects?.some((mentionableObject) => {
      return mentionableObject.objectType === "product";
    });

  const {
    products: reploProducts,
    isLoading: isLoadingReploProducts,
    fetchNextPage: fetchNextReploProductsPage,
    hasNextPage: hasNextReploProductsPage,
  } = useProductList({
    projectId,
    limit: INITIAL_ITEMS_TO_LOAD,
    search: searchQuery,
    enabled: !hasIntegrationProductTab,
  });

  const { data: integrations, isLoading: isLoadingIntegrations } =
    integrationRouter.find.useQuery(projectId ? { projectId } : skipToken);

  const integrationObjectTabs = React.useMemo(() => {
    if (!hasIntegrationObjects || mentionableObjects == null) {
      return [];
    }
    return mentionableObjects.map((mentionableObject) => ({
      value: `${mentionableObject.integrationKey}:${mentionableObject.objectType}`,
      label: `${mentionableObject.displayName}s`,
      integrationKey: mentionableObject.integrationKey,
      objectType: mentionableObject.objectType as
        | "product"
        | "collection"
        | "variant",
      displayName: `${mentionableObject.displayName}s`,
    }));
  }, [hasIntegrationObjects, mentionableObjects]);

  const showReploProductsTab = !hasIntegrationProductTab;

  const staticTabs = [
    ...(showReploProductsTab
      ? [
          {
            value: "products" as const,
            label: "Products",
            icon: <ShoppingBag className="size-3" />,
          },
        ]
      : []),
    {
      value: "assets" as const,
      label: "Assets",
      icon: <ImageIcon className="size-3" />,
    },
    {
      value: "integrations" as const,
      label: "Integrations",
      icon: <Plug className="size-3" />,
    },
  ];

  const allTabValues = [
    ...integrationObjectTabs.map((tab) => tab.value),
    ...staticTabs.map((tab) => tab.value),
  ];
  const currentTab = allTabValues.includes(storedTab)
    ? storedTab
    : allTabValues[0] ?? "products";
  const setCurrentTab = setStoredTab;

  const handleReploProductSelect = (product: Product) => {
    selectProduct(product, editor);
    onClose?.();
  };

  const handleAssetSelect = (asset: Asset) => {
    selectAsset(asset, editor);
    onClose?.();
  };

  const handleIntegrationSelect = (integrationType: IntegrationType) => {
    selectIntegration(integrationType, editor);
    onClose?.();
  };

  return (
    <div ref={menuRef} className="flex flex-col h-full">
      {!hideHeader && (
        <div className="flex flex-row justify-between items-center p-3 border-b-0.5 border-border">
          <span className="font-medium text-default text-sm">
            Add a reference
          </span>
          <Button icon variant="tertiary" size="sm" onClick={onClose}>
            <X />
          </Button>
        </div>
      )}

      <motion.div
        className="overflow-hidden max-h-[400px]"
        animate={{ height: "auto" }}
        initial={false}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        <Tabs
          value={currentTab}
          onValueChange={(value) => {
            setCurrentTab(value);
          }}
          orientation="vertical"
          className="h-full"
        >
          <div className="p-3 border-r-0.5 border-border">
            <TabsList size="sm" className="h-full">
              {integrationObjectTabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  size="sm"
                  startEnhancer={<ShoppingBag className="size-3" />}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
              {staticTabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  size="sm"
                  startEnhancer={tab.icon}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className="w-full px-1 py-3 flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex flex-row items-center gap-2 px-2">
              <Input
                placeholder="Search"
                startEnhancer={<Search size={14} />}
                size={inputSize}
                value={searchInputValue}
                onChange={(event) => setSearchInputValue(event.target.value)}
                className="w-full"
                autoFocus={true}
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    const listContainer = menuRef.current?.querySelector(
                      `[data-list-container="${currentTab}"]`,
                    ) as HTMLElement;
                    if (listContainer) {
                      listContainer.focus();
                      listContainer.dispatchEvent(
                        new KeyboardEvent("keydown", {
                          key: "ArrowDown",
                          bubbles: true,
                          cancelable: true,
                        }),
                      );
                    }
                    return;
                  }

                  if (event.key === "Escape") {
                    onClose?.();
                  }
                }}
              />
              {currentTab === "assets" && (
                <Button
                  icon
                  variant="secondary"
                  onClick={openFileDialog}
                  size="sm"
                >
                  <Upload />
                </Button>
              )}
            </div>

            {integrationObjectTabs.map((tab) => (
              <TabsContent
                key={tab.value}
                value={tab.value}
                className="flex-1 min-h-0"
              >
                <IntegrationObjectTab
                  integrationKey={tab.integrationKey}
                  objectType={tab.objectType}
                  displayName={tab.displayName}
                  tabValue={tab.value}
                  searchQuery={searchQuery}
                  isActiveTab={currentTab === tab.value}
                  scrollableTargetId={`context-menu-scroll-${tab.value}`}
                  editor={editor}
                  onClose={onClose}
                />
              </TabsContent>
            ))}

            {showReploProductsTab && (
              <TabsContent value="products" className="flex-1 min-h-0">
                <div
                  className="max-h-[340px] overflow-y-auto"
                  id="context-menu-scroll-container"
                >
                  <ProductContextItemsList
                    products={reploProducts}
                    isLoading={isLoadingReploProducts}
                    searchQuery={searchQuery}
                    fetchNextProductsPage={() => {
                      void fetchNextReploProductsPage();
                    }}
                    hasNextProductsPage={hasNextReploProductsPage}
                    onProductSelect={handleReploProductSelect}
                  />
                </div>
              </TabsContent>
            )}

            <TabsContent value="assets" className="flex-1 min-h-0">
              <div className="h-full overflow-hidden">
                <AssetContextItemsList
                  onAssetSelect={handleAssetSelect}
                  getRootProps={getRootProps}
                  getInputProps={getInputProps}
                  droppedFilesMap={droppedFilesMap}
                  isDragActive={isDragActive}
                  searchQuery={searchQuery}
                />
              </div>
            </TabsContent>
            <TabsContent
              value="integrations"
              className="flex-1 min-h-0 flex"
              UNSAFE_className="flex-col"
            >
              <div className="max-h-[340px] overflow-y-auto">
                <IntegrationContextItemsList
                  integrations={integrations ?? []}
                  isLoading={isLoadingIntegrations}
                  searchQuery={searchQuery}
                  onIntegrationSelect={handleIntegrationSelect}
                />
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </motion.div>
    </div>
  );
};
